import { FormInputNumber, Hint, RadioGroup } from '@systemeio/ui'
import {
  MailingSplitTestWinnerTypeEnum,
  mailingSplitTestWinnerLocoKeys,
} from 'modules/newsletters/split-test/enum/mailing-split-test-winner-type-enum'
import {
  NewsletterSplitTestErrorType,
  SplitTestSettingsInterface,
} from 'modules/newsletters/split-test/types/newsletter-split-test-interface'
import React, { useState } from 'react'
import TimeDelayPicker from 'shared/components/date-picker/time-delay-picker'
import { TimeType } from 'shared/components/date-picker/time-delay-picker/constants'
import {
  convertSeconds,
  convertSecondsToTimeValue,
  convertTimeValueToSeconds,
  convertToSeconds,
} from 'shared/components/date-picker/time-delay-picker/utils'
import { DisclosureBox } from 'shared/components/disclosure-box'
import { FieldLabel } from 'shared/components/form/field-label'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'

interface SplitTestSettingsProps {
  splitTestSettings: SplitTestSettingsInterface
  onChange: (settings: SplitTestSettingsInterface) => void
  errors: NewsletterSplitTestErrorType['settings']
  disabled: boolean
  isPreFetching: boolean
}

export function SplitTestSettings({
  splitTestSettings,
  onChange,
  errors,
  disabled,
  isPreFetching,
}: SplitTestSettingsProps) {
  const { t } = useLocoTranslation()
  const [selectedTime, setSelectedTime] = useState(
    convertSecondsToTimeValue(convertToSeconds(splitTestSettings.durationInHours, TimeType.HOURS)),
  )

  const options = Object.values(MailingSplitTestWinnerTypeEnum).map(id => ({
    caption: t(mailingSplitTestWinnerLocoKeys[id]),
    id,
  }))

  const isError = !errors?.winnerType || !errors?.emailRatio || !errors?.durationInHours

  return (
    <DisclosureBox
      defaultOpen
      title={
        <b className={`text-text-primary text-start text-[17px]`}>
          {t('dashboard.newsletter_editor.split_test.settings.title')}
        </b>
      }
      wrapperClassName={twMerge(isError && 'border border-danger rounded-lg')}
    >
      <div className={'flex flex-col gap-5'}>
        <TimeDelayPicker
          isPreFetching={isPreFetching}
          disabled={disabled}
          allowedOptions={[TimeType.DAYS, TimeType.HOURS]}
          className={'w-64'}
          required
          error={errors?.durationInHours?.join('\n')}
          label={
            <Hint label={t('dashboard.newsletter_editor.split_test.duration_description')}>
              {t('dashboard.newsletter_editor.split_test.duration')}
            </Hint>
          }
          selectedTimeType={selectedTime.timeType}
          selectedTimeValue={selectedTime.timeValue}
          onChange={(value, type) => {
            setSelectedTime(props => ({ ...props, timeType: type, timeValue: value }))
            onChange({
              ...splitTestSettings,
              durationInHours: convertSeconds(
                convertTimeValueToSeconds(value, type),
                TimeType.HOURS,
              ),
            })
          }}
        />
        <div className={'flex flex-col gap-1'}>
          <FieldLabel
            required
            label={
              <Hint label={t('dashboard.newsletter_editor.split_test.contacts_requirement')}>
                {t('dashboard.newsletter_editor.split_test.email_ratio.title')}
              </Hint>
            }
          />
          <div className={'flex flex-wrap items-start gap-5'}>
            <div className={'flex items-baseline gap-3'}>
              <b className={'text-text-primary'}>
                {t('dashboard.newsletter_editor.split_test.email_ratio.test')}
              </b>
              <FormInputNumber
                isPreFetching={isPreFetching}
                disabled={disabled}
                className={'w-28'}
                value={splitTestSettings.emailRatio}
                onChange={value => onChange({ ...splitTestSettings, emailRatio: value })}
                max={100}
                addonAfter={'%'}
                error={errors?.emailRatio?.join('\n')}
                type={'integer'}
              />
            </div>
            <div className={'flex items-baseline gap-3'}>
              <b className={'text-text-primary'}>
                {t('dashboard.newsletter_editor.split_test.email_ratio.winner')}
              </b>
              <FormInputNumber
                isPreFetching={isPreFetching}
                disabled={disabled}
                className={'w-28'}
                value={100 - splitTestSettings.emailRatio}
                onChange={value => onChange({ ...splitTestSettings, emailRatio: 100 - value })}
                max={100}
                addonAfter={'%'}
                type={'integer'}
              />
            </div>
          </div>
        </div>
        <RadioGroup
          disabled={disabled || isPreFetching}
          required
          error={errors?.winnerType?.join('\n')}
          label={t('dashboard.newsletter_editor.split_test.determinate.title')}
          options={options}
          selected={splitTestSettings.winnerType}
          onChange={winnerType => onChange({ ...splitTestSettings, winnerType })}
        />
      </div>
    </DisclosureBox>
  )
}
